<template>
    <div class="edu-item">
        <div class="row mobile-block-mb">
            <div class="col-1">
                <div style="padding-bottom: 1px">
                    <ModelSelect
                        label="Уровень образования"
                        class="model"
                        item-value="id"
                        item-text="field_value"
                        :items="dictionaryByType('education_level')"
                        :value="edu.foreignLevelId"
                        @change="updateField('foreignLevelId', $event)"
                    />
                </div>
                <div>
                    <ModelAutoComplete
                        label="Город"
                        :value="edu.city"
                        @change="updateField('city', $event)"
                        :vizKeyPath="[['value']]"
                        :items="autoCompleteAddresses"
                        @debounce-end="getAutocompleteAddress"
                        @selected="getFullAddress"
                        @clear="updateField('city', null)"
                    />
                </div>
            </div>

            <div class="col-2">
                <ModelString
                    v-if="isNotUniversityFromCatalog"
                    label="Учебное заведение"
                    :value="edu.educationPlace"
                    isMultipleLines
                    :input-filter="placeInvalidator"
                    @change="updateField('educationPlace', $event)"
                />
                <ModelAutoComplete
                    v-else
                    label="Учебное заведение"
                    :value="edu.foreignPlaceId"
                    :itemSelected="foreignPlaceSelected"
                    @change="updateField('foreignPlaceId', $event)"
                    :vizKeyPath="[['field_value']]"
                    :valKeyPath="[['id']]"
                    isMultipleLines
                    :items="institutions"
                    @debounce-end="findInstitutions"
                    @clear="updateField('foreignPlaceId', null)"
                />
                <ModelCheckbox
                    v-if="isHighEducation && !specialStudyPlace"
                    class="edu-checkbox"
                    :input-value="edu.isNotUniversityFromCatalog"
                    @change="updateField('isNotUniversityFromCatalog', $event)"
                    label="Мое учебное заведение отсутствует в справочнике, я укажу название вручную"
                />
            </div>
        </div>

        <div class="row align-bottom">
            <ModelISOYM
                class="col-1 label mobile-block-mb"
                label="Дата начала"
                :value="edu.dateStart"
                @change="updateField('dateStart', $event)"
            />

            <ModelCheckbox
                v-if="isMobile"
                class="edu-checkbox"
                :input-value="edu.isNow"
                @change="updateField('isNow', $event)"
                label="По настоящее время"
            />

            <ModelISOYM
                class="col-1 label"
                :value="edu.dateEnd"
                @change="updateField('dateEnd', $event)"
                label="Дата окончания"
                :disabled="edu.isNow"
            />

            <div class="col-1">
                <ModelSelect
                    v-if="isHighEducation"
                    label="Номер курса"
                    class="model"
                    item-value="value"
                    item-text="text"
                    :items="courseNumberItems"
                    :value="edu.courseNumber"
                    @change="updateField('courseNumber', $event)"
                />
            </div>
        </div>

        <ModelCheckbox
            v-if="!isMobile"
            class="edu-checkbox"
            :input-value="edu.isNow"
            @change="updateField('isNow', $event)"
            label="По настоящее время"
        />

        <div class="row align-bottom" v-if="isHighEducation">
            <ModelString
                class="col-1"
                label="Факультет / Институт / Отделение"
                :value="edu.faculty"
                @change="updateField('faculty', $event)"
            />

            <ModelSelect
                label="Форма обучения"
                class="model col-1"
                item-value="id"
                item-text="field_value"
                :items="dictionaryByType('form_of_study')"
                :value="edu.foreignEducationFormId"
                @change="updateField('foreignEducationFormId', $event)"
                :max-length="500"
            />

            <ModelString
                class="col-1"
                label="Наименование образовательной программы"
                :value="edu.educationProgram"
                @change="updateField('educationProgram', $event)"
            />
        </div>

        <div v-if="showClass" class="row">
            <ModelString
                class="col-1"
                label="Класс"
                :value="edu.schoolClass"
                :input-filter="schoolClassValidator"
                @change="updateField('schoolClass', $event)"
            />
        </div>

        <div v-if="!showClass" class="row">
            <template v-if="!!educationLevel">
                <ModelString
                    v-if="isForeignLevel"
                    class="col-1"
                    label="Специальность / Профессия / Направление"
                    :value="edu.specialtySpecial"
                    @change="updateField('specialtySpecial', $event)"
                />

                <ModelAutoComplete
                    v-else
                    class="col-1"
                    label="Специальность / Профессия / Направление"
                    :value="edu.foreignSpecialtyId"
                    :itemSelected="specialtySelected"
                    @change="updateField('foreignSpecialtyId', $event)"
                    :vizKeyPath="[['field_value']]"
                    :valKeyPath="[['id']]"
                    :items="specialty"
                    @debounce-end="findSpecialty"
                    @clear="updateField('foreignSpecialtyId', null)"
                />

                <ModelString
                    class="col-1"
                    label="Квалификация"
                    :value="edu.qualification"
                    @change="updateField('qualification', $event)"
                />

                <ModelSelect
                    v-if="!!educationLevel"
                    class="col-1"
                    label="Ученая степень"
                    item-value="id"
                    item-text="field_value"
                    :items="dictionaryByType('academic_degree')"
                    :value="edu.foreignDegreeId"
                    @change="updateField('foreignDegreeId', $event)"
                />
            </template>
        </div>

        <div v-if="!showClass" class="row">
            <ModelFile
                class="col-1 edu-file"
                label="Подтверждающий документ"
                :value="edu.document"
                @change="updateField('document', $event ? $event : null)"
            />

            <div class="col-1"></div>
        </div>
        <ModelCheckbox
            v-if="isHasHonor"
            :input-value="edu.isHonorsDegree"
            :label="honorDergeeLabel"
            class="edu-checkbox"
            @change="updateField('isHonorsDegree', $event)"
        />
    </div>
</template>

<script>
import ModelString from '@/components/UI/models/ModelString';
import ModelSelect from '@/components/UI/models/ModelSelect';
import ModelAutoComplete from '@/components/UI/models/ModelAutoComplete';
import ModelISOYM from '@/components/UI/models/ModelISOYM';
import ModelFile from '@/components/UI/models/ModelFile';
import ModelCheckbox from '@/components/UI/models/ModelCheckbox';

import { mapActions, mapGetters } from 'vuex';
import { post, qs } from '@/tools/http';

const getters = ['dictionaryByType', 'isMobile'];

const actions = ['dictionaryAutocomplete', 'loadDictionaryItems', 'getDictionaryItemById'];

export default {
    name: 'WideEduItem',

    components: {
        ModelString,
        ModelISOYM,
        ModelFile,
        ModelCheckbox,
        ModelSelect,
        ModelAutoComplete,
    },

    model: {
        prop: 'edu',
        event: 'change',
    },

    props: {
        edu: Object,
    },

    data: () => ({
        autoCompleteAddresses: null,

        specialty: [],
        specialtySelected: null,

        institutions: [],
        foreignPlaceSelected: null,

        schoolClassValidator: (value) => {
            return !value || (isFinite(value) && value ^ 0 && value >= 1 && value <= 11);
        },
        placeInvalidator: (value) => {
            return !value || (value && value.length <= 255);
        },
    }),

    computed: {
        ...mapGetters(getters),

        educationLevel() {
            const levelList = this.dictionaryByType('education_level');
            return levelList && this.edu
                ? levelList.find((el) => String(el.id) === String(this.edu.foreignLevelId))
                : null;
        },

        showClass() {
            const enableOptions = ['base_general', 'middle_general'];
            return this.educationLevel && enableOptions.includes(this.educationLevel.field_name);
        },

        specialStudyPlace() {
            const enableOptions = ['add', 'base_general', 'middle_general', 'foreign'];
            return this.educationLevel && enableOptions.includes(this.educationLevel.field_name);
        },

        isNotUniversityFromCatalog() {
            return this.specialStudyPlace || this.edu.isNotUniversityFromCatalog;
        },

        isForeignLevel() {
            return this.educationLevel.field_name === 'foreign';
        },

        isHasHonor() {
            return this.edu && !!this.edu.foreignLevelId;
        },
        honorDergeeLabel() {
            const certificateFields = ['base_general', 'middle_general'];

            return this.educationLevel && certificateFields.includes(this.educationLevel.field_name)
                ? 'Аттестат с отличием'
                : 'Диплом с отличием';
        },

        courseNumberItems() {
            if (!this.educationLevel) {
                return [];
            }
            let numbers;
            switch (this.educationLevel.field_name) {
                case 'hi_master':
                case 'specializirovannoe_obrazovanie':
                    numbers = [1, 2, 3];
                    break;
                case 'hi_bachelor':
                case 'hi_master_or_specialist':
                case 'hi_prof_cadr':
                case 'bazovoe_obrazovanie':
                case 'foreign':
                case 'add':
                case 'middle_profi':
                case 'profi':
                    numbers = [1, 2, 3, 4, 5, 6];
                    break;
                case 'hi_specialist':
                    numbers = [1, 2, 3, 4, 5, 6, 7];
                    break;
                case 'Aspirantura':
                    numbers = [1, 2, 3, 4, 5];
                    break;
                default:
                    numbers = [];
            }

            return numbers.map((n) => ({ text: n.toString(), value: n }));
        },

        isHighEducation() {
            const enableOptions = [
                'hi_bachelor',
                'hi_master',
                'hi_master_or_specialist',
                'hi_specialist',
                'hi_prof_cadr',
                'foreign',
                'add',
                'middle_profi',
                'profi',
                'bazovoe_obrazovanie',
                'specializirovannoe_obrazovanie',
                'Aspirantura',
            ];
            return this.educationLevel && enableOptions.includes(this.educationLevel.field_name);
        },
    },

    async mounted() {
        this.loadDictionaryItems('education_level');
        this.loadDictionaryItems('academic_degree');
        this.loadDictionaryItems('form_of_study');
        this.loadDictionaryItems('okso');

        if (this.edu.foreignSpecialtyId) {
            this.specialtySelected = await this.getDictionaryItemById(this.edu.foreignSpecialtyId);
        }
        if (this.edu.foreignPlaceId) {
            this.foreignPlaceSelected = await this.getDictionaryItemById(this.edu.foreignPlaceId);
        }
    },

    methods: {
        ...mapActions(actions),
        async getAutocompleteAddress(inputObj) {
            if (inputObj.value === '') {
                this.autoCompleteAddresses = [];
                return;
            }
            let postParams = [
                'dadata',
                'search',
                qs({ query: inputObj.value, type: 'address' }),
                true,
                { 'content-type': 'application/x-www-form-urlencoded' },
            ];
            let res = await post(...postParams);
            this.autoCompleteAddresses = res.suggestions;
        },
        async getFullAddress(item) {
            if (item.value === '') {
                this.edu.city.data.federal_district = null;
                return;
            }
            let postParams = [
                'dadata',
                'clean',
                qs({ query: item.value, type: 'address' }),
                true,
                { 'content-type': 'application/x-www-form-urlencoded' },
            ];
            let res = await post(...postParams);
            this.edu.city.data.federal_district = res.suggestions[0].data.federal_district;
        },
        async findSpecialty({ value }) {
            // this.specialty = this.dictionaryByType('education_specialities')
            //     .filter((sp) => {
            //         return sp['field_value'].indexOf(inputObj.value) > -1;
            //     });
            this.specialty = await this.dictionaryAutocomplete({
                type: 'education_specialities',
                search: value,
            });
        },
        async findInstitutions({ value }) {
            this.institutions = await this.dictionaryAutocomplete({
                type: 'educational_institutions',
                search: value,
            });
        },

        updateField(key, value) {
            const dataToUpdate = key instanceof Object ? key : { [key]: value };
            const updatedForm = { ...this.edu };

            for (const fieldKey in dataToUpdate) {
                let fieldValue = dataToUpdate[fieldKey];

                if (fieldKey === 'schoolClass') {
                    fieldValue = fieldValue ? parseInt(fieldValue) : null;
                }

                updatedForm[fieldKey] = fieldValue;
            }

            if (dataToUpdate.isNotUniversityFromCatalog === undefined) {
                updatedForm.isNotUniversityFromCatalog = this.isNotUniversityFromCatalog;
            }

            this.$emit('change', updatedForm);
        },
    },

    watch: {
        async 'edu.foreignPlaceId'(current) {
            this.foreignPlaceSelected = current ? await this.getDictionaryItemById(current) : null;
        },
        async 'edu.foreignSpecialtyId'(current) {
            this.specialtySelected = current ? await this.getDictionaryItemById(current) : null;
        },
        'educationLevel.id'(current, prev) {
            if (!prev || !current) {
                return;
            }

            const dataToUpdate = {
                courseNumber: null,
            };

            if (!this.isHighEducation || this.specialStudyPlace) {
                dataToUpdate.foreignPlaceId = null;
                dataToUpdate.isNotUniversityFromCatalog = null;
            } else if (this.edu.isNotUniversityFromCatalog === null) {
                dataToUpdate.isNotUniversityFromCatalog = false;
            }

            this.updateField(dataToUpdate);
        },
        'edu.isNotUniversityFromCatalog'(current, prev) {
            if (prev === current) {
                return;
            }

            if (current) {
                this.updateField('foreignPlaceId', null);
            }
        },
    },
};
</script>

<style lang="scss" scoped>
@import '../../../../styles/rem';
@import '../../../../styles/mrem';

.titles {
    display: flex;

    > * {
        width: 26.3rem;
        font-size: 0.7rem;
        font-weight: 600;
        margin: 0.8rem 0;
    }
}

.row {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;

    .col-1 {
        width: rem(471);
    }

    .col-2 {
        width: rem(974);
    }

    &.align-bottom {
        align-items: flex-end;
    }

    label {
        display: flex;
        align-items: center;
    }

    input {
        width: 0.6rem;
        height: 0.6rem;
    }
}

.separate {
    margin: 1rem 0;
}
@import '~@/styles/variables';

@media (max-width: $vm) {
    .edu-item .row {
        flex-direction: column;

        > * {
            width: 100%;
        }

        .data-model {
            margin-bottom: mrem(32);
        }
    }

    .mobile-block-mb {
        margin-bottom: mrem(16);
    }

    .edu-checkbox {
        margin-bottom: mrem(16);
    }

    .edu-file {
        margin: mrem(16) 0 mrem(16) 0;
    }

    .date-title-mobile {
        margin-bottom: mrem(20);
        font-size: mrem(16);
        line-height: mrem(18);
        color: #98a0a9;
        font-weight: 500;
    }

    .control {
        margin-bottom: mrem(25);
        width: 50%;
        font-size: mrem(14);
        color: #98a0a9;

        input {
            width: mrem(14);
            height: mrem(14);
        }
    }
}
</style>
